import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import router from '../router'
import {
  deepSanitize,
  filterSensitiveData,
  generateRandomString,
  generateTimestamp,
  validateInput,
  isSafeUrl
} from './security'
import { refreshToken as refreshTokenApi } from '../api/auth'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || 'http://localhost:8086',
  timeout: 15000
})

// 存储请求nonce（用于防重放攻击）
// 使用Map存储nonce和timestamp的对应关系：nonce -> timestamp
const requestNonces = new Map()
const MAX_NONCE_AGE = 5 * 60 * 1000 // 5分钟

// 清理过期的nonce
setInterval(() => {
  const now = Date.now()
  for (const [nonce, timestamp] of requestNonces.entries()) {
    if (now - timestamp > MAX_NONCE_AGE) {
      requestNonces.delete(nonce)
    }
  }
}, 60000) // 每分钟清理一次

// Token 刷新相关
let isRefreshing = false // 是否正在刷新 token
let refreshSubscribers = [] // 等待 token 刷新的请求队列

// 添加等待刷新完成的请求到队列
function subscribeTokenRefresh(resolve, reject) {
  refreshSubscribers.push({ resolve, reject })
}

// 执行队列中的所有请求（刷新成功）
function onRefreshed(token) {
  refreshSubscribers.forEach(({ resolve }) => resolve(token))
  refreshSubscribers = []
}

// 执行队列中的所有请求（刷新失败）
function onRefreshFailed(error) {
  refreshSubscribers.forEach(({ reject }) => reject(error))
  refreshSubscribers = []
}

// 刷新 token
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    throw new Error('没有可用的刷新令牌')
  }

  try {
    // 调用刷新 token 接口
    const response = await refreshTokenApi(refreshToken)
    
    if (response.code === '000000' && response.data && response.data.accessToken) {
      // 更新 accessToken
      localStorage.setItem('accessToken', response.data.accessToken)
      
      // 如果返回了新的 refreshToken，也更新它
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken)
      }
      
      return response.data.accessToken
    } else {
      throw new Error(response.msg || '刷新 token 失败')
    }
  } catch (error) {
    // 刷新失败，清除所有 token
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userInfo')
    sessionStorage.removeItem('csrfToken')
    throw error
  }
}

// 获取或生成CSRF token
function getCsrfToken() {
  let token = sessionStorage.getItem('csrfToken')
  if (!token) {
    token = generateRandomString(32)
    sessionStorage.setItem('csrfToken', token)
  }
  return token
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 1. URL安全检查
    // 构建完整URL：如果url是相对路径，需要拼接baseURL
    // 场景1: baseURL = '/api', url = '/auth/rsa-public-key' -> fullUrl = '/api/auth/rsa-public-key'
    // 场景2: baseURL = 'http://localhost:8086', url = '/auth/rsa-public-key' -> fullUrl = 'http://localhost:8086/auth/rsa-public-key'
    // 场景3: baseURL = '', url = '/auth/rsa-public-key' -> fullUrl = '/auth/rsa-public-key'
    let fullUrl = config.url || ''
    const baseURL = config.baseURL || service.defaults.baseURL || ''
    
    // 如果url不是完整URL（不包含://），需要拼接baseURL
    if (fullUrl && !fullUrl.includes('://')) {
      if (baseURL) {
        // 处理baseURL和url的拼接，避免重复的斜杠
        const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL
        const path = fullUrl.startsWith('/') ? fullUrl : '/' + fullUrl
        fullUrl = base + path
      }
      // 如果没有baseURL，fullUrl保持为相对路径（以/开头），isSafeUrl会处理
    }
    
    // 如果fullUrl为空，使用默认值
    if (!fullUrl) {
      fullUrl = baseURL || '/'
    }
    
    // 安全检查：支持相对路径（如 /api/xxx）和完整URL（如 http://xxx）
    if (!isSafeUrl(fullUrl)) {
      console.error('不安全的URL:', fullUrl, 'baseURL:', baseURL, 'url:', config.url)
      return Promise.reject(new Error('请求URL不安全'))
    }

    // 2. 输入验证和清理（对GET参数和POST数据）
    if (config.params) {
      for (const key in config.params) {
        if (typeof config.params[key] === 'string') {
          const validation = validateInput(config.params[key])
          if (!validation.valid) {
            console.warn(`参数 ${key} 验证失败:`, validation.reason)
            // 可以选择拒绝请求或清理输入
            config.params[key] = deepSanitize(config.params[key])
          }
        }
      }
    }

    // 跳过对 FormData 的处理（文件上传），避免破坏文件数据
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      // 深度清理请求数据（但保留密码字段，因为密码需要加密传输）
      const sanitizedData = deepSanitize(config.data, {
        escapeHtml: false, // 不转义，因为JSON不需要HTML转义
        stripTags: true,
        removeScripts: true
      })
      config.data = sanitizedData
    }

    // 3. 添加CSRF token（仅对非GET请求）
    if (config.method && config.method.toUpperCase() !== 'GET') {
      const csrfToken = getCsrfToken()
      config.headers['X-CSRF-Token'] = csrfToken
    }

    // 4. 添加防重放攻击的nonce和时间戳
    // 对于文件下载请求（responseType为blob），不添加时间戳和nonce，以利用浏览器缓存
    const isDownloadRequest = config.responseType === 'blob' && 
                              config.url && config.url.includes('/files/download')
    
    if (!isDownloadRequest) {
      const timestamp = generateTimestamp()
      const nonce = generateRandomString(16) + timestamp.toString(36)
      config.headers['X-Request-Timestamp'] = timestamp
      config.headers['X-Request-Nonce'] = nonce
      // 存储nonce和timestamp的对应关系
      requestNonces.set(nonce, timestamp)
    }

    // 5. 从localStorage获取token
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 6. 添加安全相关的请求头
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    config.headers['X-Client-Version'] = '1.0.0'

    // 7. 过滤敏感信息（仅用于日志，不影响实际请求）
    if (process.env.NODE_ENV === 'development') {
      const filteredConfig = filterSensitiveData(config)
      console.log('请求配置（已过滤敏感信息）:', filteredConfig)
    }

    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 如果是blob类型（文件下载），直接返回，不进行JSON解析和业务状态码检查
    if (response.config.responseType === 'blob') {
      // 检查Content-Type，如果是JSON，说明可能是错误响应
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || ''
      if (contentType.includes('application/json') && res instanceof Blob) {
        // 错误响应被转换为blob，需要解析
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result)
              ElMessage.error(errorData.msg || errorData.message || '导出失败')
              reject(new Error(errorData.msg || errorData.message || '导出失败'))
            } catch (e) {
              // 解析失败，直接返回blob
              resolve(res)
            }
          }
          reader.onerror = () => resolve(res)
          reader.readAsText(res)
        })
      }
      return res
    }
    
    // 1. 检查响应头中的安全标识（如果后端有设置）
    const securityHeaders = {
      'X-Content-Type-Options': response.headers['x-content-type-options'],
      'X-Frame-Options': response.headers['x-frame-options'],
      'X-XSS-Protection': response.headers['x-xss-protection']
    }
    
    // 在开发环境中验证安全响应头
    if (process.env.NODE_ENV === 'development') {
      const missingHeaders = []
      if (!securityHeaders['X-Content-Type-Options']) {
        missingHeaders.push('X-Content-Type-Options')
      }
      if (!securityHeaders['X-Frame-Options']) {
        missingHeaders.push('X-Frame-Options')
      }
      if (missingHeaders.length > 0) {
        console.warn('响应缺少安全响应头:', missingHeaders.join(', '))
      }
    }
    
    // 2. 清理响应数据（防止XSS）
    if (res && typeof res === 'object') {
      // 对响应数据进行深度清理（但保留业务数据格式）
      // 注意：这里只清理字符串，不改变数据结构
      const sanitizedRes = deepSanitize(res, {
        escapeHtml: false, // JSON响应不需要HTML转义
        stripTags: true,
        removeScripts: true
      })
      Object.assign(res, sanitizedRes)
    }
    
    // 3. 如果响应状态码不是200，说明请求失败
    if (response.status !== 200) {
      ElMessage.error(res.msg || '请求失败')
      return Promise.reject(new Error(res.msg || '请求失败'))
    }
    
    // 4. 如果业务状态码不是成功码，说明业务处理失败
    if (res.code !== '000000') {
      // 如果是未授权，需要判断是否是登录接口或刷新 token 接口
      if (res.code === '000401') {
        const isLoginRequest = response.config.url && response.config.url.includes('/auth/login')
        const isRefreshRequest = response.config.url && response.config.url.includes('/auth/refresh')
        
        // 如果是登录接口或刷新 token 接口，直接返回错误
        if (isLoginRequest || isRefreshRequest) {
          ElMessage.error(res.msg || '未授权，请重新登录')
          if (!isLoginRequest) {
            // 刷新 token 失败，清除所有 token 并跳转到登录页
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('userInfo')
            sessionStorage.removeItem('csrfToken')
            router.push('/login')
          }
          return Promise.reject(new Error(res.msg || '未授权，请重新登录'))
        }
        
        // 其他接口遇到 401，尝试自动刷新 token
        const originalRequest = response.config
        
        // 如果已经在刷新 token，将当前请求加入队列
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh(
              (token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`
                resolve(service(originalRequest))
              },
              (error) => {
                reject(error)
              }
            )
          })
        }
        
        // 开始刷新 token
        isRefreshing = true
        
        return refreshAccessToken()
          .then((newToken) => {
            isRefreshing = false
            // 更新原请求的 token
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            // 通知队列中的请求
            onRefreshed(newToken)
            // 重试原请求
            return service(originalRequest)
          })
          .catch((error) => {
            isRefreshing = false
            onRefreshFailed(error)
            ElMessage.error(error.message || '登录已过期，请重新登录')
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('userInfo')
            sessionStorage.removeItem('csrfToken')
            router.push('/login')
            return Promise.reject(error)
          })
      } else if (res.code === 'QUOTA_EXCEEDED') {
        // 额度超限，使用弹窗提示
        console.log('检测到额度超限错误，显示弹窗:', res.msg)
        // 使用同步方式显示弹窗，确保用户能看到提示
        ElMessageBox.alert(
          res.msg || '额度已超限，无法继续操作',
          '额度超限提示',
          {
            confirmButtonText: '我知道了',
            type: 'warning',
            dangerouslyUseHTMLString: false,
            showClose: true,
            closeOnClickModal: false,
            closeOnPressEscape: true
          }
        ).then(() => {
          console.log('用户关闭了额度超限弹窗')
        }).catch(() => {
          // 用户关闭弹窗，不做任何操作
          console.log('额度超限弹窗被关闭')
        })
        // 创建一个自定义错误对象，标记已经显示过错误消息，避免重复显示
        const error = new Error(res.msg || '额度已超限，无法继续操作')
        error.isBusinessError = true // 标记为业务错误，已经显示过消息
        error.code = res.code
        return Promise.reject(error)
      } else {
        // 如果是会员相关的错误，且当前在登录页，不显示错误提示（让登录页的handleLogin来处理）
        const isLoginPage = window.location.pathname === '/login'
        const isMemberRelatedError = res.msg && (
          res.msg.includes('会员') || 
          res.msg.includes('该系统仅开放给会员用户') ||
          res.msg.includes('会员权益')
        )
        
        // 只有在非登录页，或者（是登录页但不是会员相关错误）时才显示错误
        if (!(isLoginPage && isMemberRelatedError)) {
          ElMessage.error(res.msg || '操作失败')
        }
        
        // 创建一个自定义错误对象，标记已经显示过错误消息，避免重复显示
        const error = new Error(res.msg || '操作失败')
        error.isBusinessError = true // 标记为业务错误，已经显示过消息
        error.code = res.code
        return Promise.reject(error)
      }
    }
    
    return res
  },
  error => {
    console.error('响应错误:', error)
    
    // 过滤敏感信息后记录错误
    if (process.env.NODE_ENV === 'development') {
      const filteredError = filterSensitiveData(error)
      console.error('响应错误详情（已过滤敏感信息）:', filteredError)
    }
    
    if (error.response) {
      const { status, data, config } = error.response
      
      // 如果是blob类型的请求，错误响应可能是JSON格式，需要解析
      if (config.responseType === 'blob' && data instanceof Blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result)
              ElMessage.error(errorData.msg || errorData.message || '导出失败')
              reject(new Error(errorData.msg || errorData.message || '导出失败'))
            } catch (e) {
              ElMessage.error('导出失败')
              reject(new Error('导出失败'))
            }
          }
          reader.onerror = () => {
            ElMessage.error('导出失败')
            reject(new Error('导出失败'))
          }
          reader.readAsText(data)
        })
      }
      
      if (status === 401) {
        const originalRequest = config
        const isLoginRequest = originalRequest.url && originalRequest.url.includes('/auth/login')
        const isRefreshRequest = originalRequest.url && originalRequest.url.includes('/auth/refresh')
        
        // 如果是登录接口或刷新 token 接口，直接返回错误
        if (isLoginRequest || isRefreshRequest) {
          ElMessage.error('未授权，请重新登录')
          if (!isLoginRequest) {
            // 刷新 token 失败，清除所有 token 并跳转到登录页
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('userInfo')
            sessionStorage.removeItem('csrfToken')
            router.push('/login')
          }
          return Promise.reject(error)
        }
        
        // 其他接口遇到 401，尝试自动刷新 token
        // 如果已经在刷新 token，将当前请求加入队列
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh(
              (token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`
                resolve(service(originalRequest))
              },
              (error) => {
                reject(error)
              }
            )
          })
        }
        
        // 开始刷新 token
        isRefreshing = true
        
        return refreshAccessToken()
          .then((newToken) => {
            isRefreshing = false
            // 更新原请求的 token
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            // 通知队列中的请求
            onRefreshed(newToken)
            // 重试原请求
            return service(originalRequest)
          })
          .catch((error) => {
            isRefreshing = false
            onRefreshFailed(error)
            ElMessage.error(error.message || '登录已过期，请重新登录')
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('userInfo')
            sessionStorage.removeItem('csrfToken')
            router.push('/login')
            return Promise.reject(error)
          })
      } else if (status === 403) {
        ElMessage.error('无权限访问该资源')
      } else if (status === 429) {
        ElMessage.error('请求过于频繁，请稍后再试')
      } else {
        // 如果是参数验证错误（code === '000002'），不在这里显示错误消息
        // 让业务代码来处理，以便可以弹窗显示详细的验证错误信息
        if (data?.code !== '000002') {
          // 如果是会员相关的错误，且当前在登录页，不显示错误提示（让登录页的handleLogin来处理）
          const isLoginPage = window.location.pathname === '/login'
          const isMemberRelatedError = data?.msg && (
            data.msg.includes('会员') || 
            data.msg.includes('该系统仅开放给会员用户') ||
            data.msg.includes('会员权益')
          )
          
          // 只有在非登录页，或者（是登录页但不是会员相关错误）时才显示错误
          if (!(isLoginPage && isMemberRelatedError)) {
            ElMessage.error(data?.msg || data?.message || '请求失败')
          }
        }
      }
    } else if (error.request) {
      ElMessage.error('网络错误，请检查网络连接')
    } else {
      ElMessage.error('请求配置错误')
    }
    return Promise.reject(error)
  }
)

export default service

